import { get } from "lodash";
import Util from "@lululemon/ecom-common/src/lib/util";
import { log } from "@lululemon/mwa-utils";

import { getFeatureCDP } from "@lululemon/ecom-common/src/util/helpers";

import {
  getAnalyticsTextFormat,
  parseToNumber,
  getLocalePrefix,
  getCDPGender,
  getPDPGender,
  getAppliedFilter,
  trackEvent,
} from "@lululemon/ecom-common/src/lib/analytics";

const util = new Util("ecom-cdp/analytics.js");
const $window = util.windowEvent();

export const trackCDPBopis = ({ storeName = "", toggleStatus = false }) => {
  const checkStoreLinkName = "bopis-check-store-availability";
  const toggleName = `bopis-store-availability-toggle-${toggleStatus}`;

  trackEvent("store-availability", {
    name: toggleStatus ? toggleName : checkStoreLinkName,
    event: {
      store: storeName,
    },
    version: "1.0",
  });
};

export const getSelectedFilters = ({ facets }) => {
  let parsedAppliedFilters = [];
  const parsedAppliedFiltersWithCount = [];

  facets?.forEach(({ name, refinements, "selected-count": selectedCount }) => {
    if (selectedCount <= 0 || name === "Sort By:") return;
    parsedAppliedFiltersWithCount.push(`${name}:${selectedCount}`);
    refinements.forEach((facet) => {
      if (facet.isSelected) {
        const appliedFilter = {
          selectedFilter: name,
          name: facet.label,
        };
        parsedAppliedFilters = [appliedFilter, ...parsedAppliedFilters];
      }
    });
  });
  return { parsedAppliedFiltersWithCount, parsedAppliedFilters };
};

export function updatePageType(pageType) {
  if (
    $window.digitalData &&
    $window.digitalData.page &&
    $window.digitalData.page.category
  ) {
    $window.digitalData.page.category.pageType = pageType;
  }
}

// Updates window.digitalData.product with the products show on the page
export function updateProductsForCDP(products = [], sizeFilter = "") {
  if (!$window.digitalData) return;

  const productInfoArray = products.map((product) => {
    if (!product) {
      log.error(
        "Error updating analytics for CDP - product is merchandised incorrectly"
      );
    }

    let productInfo;
    // Out of stock - This scenario is specific to the Feature Tile
    const productId = get(product, "id", "");
    if (productId) {
      const salePrice = get(product, "salePrice");
      const listPrice = parseToNumber(get(product, "listPrice", 0));
      productInfo = {
        categoryUnifiedId: "", // we don't have data for this for OOS. Analytics team prefers ''
        color: get(product, "color", ""),
        feature: getFeatureCDP(product),
        fullPrice: listPrice,
        name: get(product, "name", ""),
        onSale: !!get(product, "salePrice", false),
        price: salePrice ? parseToNumber(salePrice) : listPrice,
        productCategory: get(product, "productCategoryDisplayName", ""),
        productId,
        size: sizeFilter.length ? sizeFilter : "",
        skuAvailable: "", // unknown until the user enters PDP but analytics team wants to keep
        skuId: `${getLocalePrefix()}${get(product, "skuId", "")}`,
        styleNumber: util.getParams(get(product, "pdpUrl", ""), "color"),
      };
    } else {
      const {
        "color-name": color = "",
        size = "",
        sku = "",
      } = get(product, "sku-style-order[0]", "");
      productInfo = {
        categoryUnifiedId: get(product, "parent-category-unified-id", ""),
        color,
        feature: getFeatureCDP(product),
        fullPrice: parseToNumber(get(product, "list-price", 0)),
        name: get(product, "display-name", ""),
        onSale: get(product, "product-on-sale") === "1",
        price: parseToNumber(
          get(product, "product-sale-price[0]", get(product, "list-price", 0))
        ),
        productCategory: get(product, "parent-category-display-name", ""),
        productId: get(product, "repository-id", ""),
        size: size.trim(),
        skuAvailable: "", // unknown until the user enters PDP but analytics team wants to keep
        skuId: sku,
        styleNumber: get(product, "sku-style-number", []),
      };
    }

    return { productInfo };
  });

  $window.digitalData.product = productInfoArray;
}

export const getAnalytics2Dot1 = (type, name = "", position) => {
  try {
    const formattedName = getAnalyticsTextFormat(name);

    return {
      type: "component",
      component: {
        type,
        name: formattedName,
        id: formattedName,
      },
      attributes: {
        position,
      },
      version: 2.1,
    };
  } catch (e) {
    log.error(`#getAnalytics2Dot1 Error: ${e}`);
  }
};

export const setAppliedFilters = (type, value) => {
  const selectedFilter = `${type}:${value}`.toLowerCase();

  const appliedFilters =
    $window?.digitalData?.page?.attributes?.appliedFilters || [];

  const isFilterApplied = appliedFilters.includes(selectedFilter);

  if (!isFilterApplied) {
    appliedFilters.push(selectedFilter);
  } else {
    const filteredList = appliedFilters.filter(
      (filter) => filter !== selectedFilter
    );
    $window.digitalData.page.attributes.appliedFilters = filteredList;
  }
};

export const formatFacetNavAppliedFilters = (
  appliedFilters = [],
  expandedPanels = {}
) => {
  let formattedFilters = [];
  // eslint-disable-next-line array-callback-return
  appliedFilters.forEach((filter) => {
    const formattedVisibility = expandedPanels[filter.selectedFilter]
      ? "visible"
      : "hidden";
    const formattedFilter =
      `${filter?.selectedFilter}:${filter?.name}:${formattedVisibility}`.toLowerCase();
    const newFilter = formattedFilters.indexOf(formattedFilter) < 0;
    if (newFilter) {
      formattedFilters = [...formattedFilters, formattedFilter];
    }
  });
  return formattedFilters;
};

export const trackMobileFilterClick = ({
  appliedFilters,
  appliedFiltersWithCount,
  totalNumberOfProducts,
}) => {
  const expandedPanels =
    JSON.parse(window?.sessionStorage?.getItem("expandedFacets") || null) || {};
  trackEvent("cdp-filters-submission", {
    page: {
      attributes: {
        appliedFilters: formatFacetNavAppliedFilters(
          appliedFilters,
          expandedPanels
        ),
        appliedFilterCount: appliedFiltersWithCount,
        appliedFilterResults: totalNumberOfProducts,
      },
    },
    version: 1.0,
  });
};

export function updateCategoryDetails(pageType, pageData) {
  let primaryCategory;
  let subCategory1;
  let subCategory2;
  if ($window.digitalData) {
    try {
      // Checks if its pulling from the CDP or PDP
      const isPDP = !!pageData["product-summary"];
      const gender = isPDP ? getPDPGender(pageData) : getCDPGender(pageData);
      const categoryRefinementCrumb = isPDP
        ? pageData["refinement-crumbs"]
        : pageData?.refinementCrumbs.find(
            (crumb) => crumb["dimension-name"] === "product.category"
          );
      const appliedFiltersCDP = $window.digitalData.page?.attributes
        ?.appliedFilters
        ? $window.digitalData.page.attributes.appliedFilters
        : [];

      if (pageData.fusionQueryId && !isPDP) {
        $window.digitalData.page.attributes = {
          cdp: {
            queryId: pageData.fusionQueryId,
          },
          appliedFilters: appliedFiltersCDP,
        };
      } else if (!pageData.fusionQueryId && !isPDP) {
        $window.digitalData.page.attributes = {
          appliedFilters: appliedFiltersCDP,
        };
      }
      if (categoryRefinementCrumb?.ancestors?.length === 1) {
        primaryCategory = categoryRefinementCrumb?.ancestors[0]
          ? categoryRefinementCrumb.ancestors[0].label
          : "";
        subCategory1 = categoryRefinementCrumb.label;
        subCategory2 = "";
      } else if (categoryRefinementCrumb?.ancestors?.length === 0) {
        primaryCategory = categoryRefinementCrumb.label;
        subCategory1 = "";
        subCategory2 = "";
      } else {
        primaryCategory = categoryRefinementCrumb?.ancestors?.[0]
          ? categoryRefinementCrumb.ancestors[0].label
          : "";
        subCategory1 = categoryRefinementCrumb?.ancestors?.[1]
          ? categoryRefinementCrumb.ancestors[1].label
          : "";
        subCategory2 = categoryRefinementCrumb?.label || "";
      }
      // Left existing functionality and will be removed after confirming with Analytics
      const appliedFilters =
        pageType.split(":")[0] === "cdp" ? getAppliedFilter(pageData) : "";
      const pageInfo = {
        gender,
        primaryCategory: primaryCategory.toLowerCase(),
        subCategory1,
        subCategory2,
        appliedFilters,
        pageType,
        categoryName: pageData.h1Title,
      };
      $window.digitalData.page.category = pageInfo;
    } catch (err) {
      console.error("digitaldata error: ", err);
    }
  }
}

export const updateRightmostCarouselItemReached = (item) => {
  try {
    if ($window.digitalData?.page?.attributes?.cdp) {
      $window.digitalData.page.attributes.cdp.currentCarouselItem = item;
    } else if ($window.digitalData?.page?.attributes) {
      $window.digitalData.page.attributes.cdp = { currentCarouselItem: item };
    } else {
      throw new Error("Digital Data object is not properly set");
    }
  } catch (e) {
    console.error("Update Current Carousel Item Error", e);
  }
};
